import * as THREE from "three";
import { assetUrl } from '../../config.js';

// Unlit (MeshBasic) scenery ignores the sun and moon, so time of day has to dim it by hand.
const UNLIT_BRIGHTNESS = { Nighttime: 0.2, Sunrise: 0.65, Sunset: 0.65 };

export class Cityscape extends THREE.Group {
  constructor() {
    super();

    this._unlitMaterials = [];
    this._scratchColor = new THREE.Color();
    this.distance = 100; // Distance from room center
    this.floorHeight = 18; // 6th floor (3m per floor)

    // Create ground first
    this.createGround();

    // Create streets
    this.createStreets();

    // Create actual 3D buildings
    this.createBuildings();

    // Add smaller details
    this.createGroundDetails();

    // Add Feydom billboard
    this.createBillboard();
  }

  createBuildings() {
    // Building materials - using MeshStandardMaterial for proper lighting and shadows
    const materials = [
      new THREE.MeshStandardMaterial({ color: 0x606060, roughness: 0.9, metalness: 0.1 }), // Gray
      new THREE.MeshStandardMaterial({ color: 0x5a4a45, roughness: 0.95, metalness: 0.0 }), // Brick
      new THREE.MeshStandardMaterial({ color: 0x707070, roughness: 0.85, metalness: 0.2 }), // Light gray
      new THREE.MeshStandardMaterial({ color: 0x4a5055, roughness: 0.9, metalness: 0.1 }), // Blue-gray
    ];

    // Buildings aligned with city blocks (between streets)
    const blockSize = 50; // Matches street spacing
    const minDistance = 60; // Don't place buildings too close to the room

    for (let x = -6; x <= 6; x++) {
      for (let z = -6; z <= 6; z++) {
        // Skip the center (where the room is)
        const distanceFromCenter = Math.sqrt(x * x + z * z);
        if (distanceFromCenter < 1.5) continue;

        // Position in the center of city blocks, offset from streets
        const blockOffsetX = 10; // Offset from street center
        const blockOffsetZ = 10;
        const posX = x * blockSize + (Math.random() - 0.5) * blockOffsetX;
        const posZ = z * blockSize + (Math.random() - 0.5) * blockOffsetZ;

        // Random building parameters - heights vary around 6th floor
        const width = 12 + Math.random() * 12;
        const depth = 12 + Math.random() * 12;
        // Buildings range from 2-12 floors (6-36m), centered around 6th floor
        const height = 10 + Math.random() * 40;

        // Create building
        const geometry = new THREE.BoxGeometry(width, height, depth);
        const material = materials[Math.floor(Math.random() * materials.length)];

        const building = new THREE.Mesh(geometry, material);

        // Position on ground (which is below us)
        building.position.set(
          posX,
          -this.floorHeight + height / 2, // Ground is 18m below room
          posZ
        );

        // Enable shadows for realistic lighting
        building.castShadow = true;
        building.receiveShadow = true;
        building.userData.isBuilding = true;
        // Store original color for day/night cycle
        building.userData.originalColor = material.color.getHex();

        this.add(building);

        // Add fewer windows for performance
        this.addWindows(building, width, height, depth);
      }
    }

    // Add smaller buildings to fill gaps
    for (let i = 0; i < 40; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 80 + Math.random() * 150;
      const posX = Math.cos(angle) * distance;
      const posZ = Math.sin(angle) * distance;

      const width = 6 + Math.random() * 6;
      const depth = 6 + Math.random() * 6;
      const height = 8 + Math.random() * 20;

      const geometry = new THREE.BoxGeometry(width, height, depth);
      const material = materials[Math.floor(Math.random() * materials.length)];
      const building = new THREE.Mesh(geometry, material);

      building.position.set(
        posX,
        -this.floorHeight + height / 2,
        posZ
      );

      building.castShadow = true;
      building.receiveShadow = true;
      building.userData.isBuilding = true;
      // Store original color for day/night cycle
      building.userData.originalColor = material.color.getHex();

      this.add(building);

      // Add windows to smaller buildings too
      if (Math.random() > 0.3) {
        this.addWindows(building, width, height, depth);
      }
    }
  }

  addWindows(building, width, height, depth) {
    // Fewer, simpler windows for performance
    const windowsX = Math.max(2, Math.floor(width / 5));
    const windowsY = Math.max(3, Math.floor(height / 8));

    const windowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffdd77,
    });

    // Only front and back faces - skip sides for performance
    for (let i = 0; i < windowsX; i++) {
      for (let j = 1; j < windowsY; j++) {
        if (Math.random() > 0.7) { // Even fewer lit windows
          const windowSize = 0.5;
          const windowGeo = new THREE.PlaneGeometry(windowSize, windowSize);
          const window1 = new THREE.Mesh(windowGeo, windowMaterial);

          window1.position.set(
            -width / 2 + (i + 0.5) * (width / windowsX),
            -height / 2 + (j + 0.5) * (height / windowsY),
            depth / 2 + 0.01
          );

          building.add(window1);

          // Back face only
          const window2 = new THREE.Mesh(windowGeo.clone(), windowMaterial.clone());
          window2.position.set(
            -width / 2 + (i + 0.5) * (width / windowsX),
            -height / 2 + (j + 0.5) * (height / windowsY),
            -depth / 2 - 0.01
          );
          window2.rotation.y = Math.PI;
          building.add(window2);
        }
      }
    }
  }

  createGround() {
    // Create a large ground plane below the room
    const groundGeometry = new THREE.PlaneGeometry(800, 800, 1, 1);

    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2520, // Brownish ground color
      roughness: 1.0,
      metalness: 0.0
    });

    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -this.floorHeight; // Ground is 18m below room (6 floors)
    ground.receiveShadow = true; // Ground receives shadows from buildings
    ground.userData.isGround = true;

    this.add(ground);
  }

  createStreets() {
    // Create a street grid
    const streetMaterial = this._unlitMaterial(0x3a3a3a); // Dark asphalt
    const streetWidth = 6;

    // Vertical streets (running north-south)
    for (let x = -300; x <= 300; x += 50) {
      const street = new THREE.Mesh(
        new THREE.PlaneGeometry(streetWidth, 800),
        streetMaterial
      );
      street.rotation.x = -Math.PI / 2;
      street.position.set(x, -this.floorHeight + 0.01, 0);
      this.add(street);
    }

    // Horizontal streets (running east-west)
    for (let z = -300; z <= 300; z += 50) {
      const street = new THREE.Mesh(
        new THREE.PlaneGeometry(800, streetWidth),
        streetMaterial
      );
      street.rotation.x = -Math.PI / 2;
      street.position.set(0, -this.floorHeight + 0.01, z);
      this.add(street);
    }
  }

  createGroundDetails() {
    // Add tall trees at the horizon edge
    this.addHorizonTrees();

    // Add parking lots closer to buildings
    const parkingMaterial = this._unlitMaterial(0x505050);

    for (let i = 0; i < 6; i++) {
      const parkingWidth = 20 + Math.random() * 10;
      const parkingDepth = 15 + Math.random() * 10;
      const parking = new THREE.Mesh(
        new THREE.PlaneGeometry(parkingWidth, parkingDepth),
        parkingMaterial
      );
      parking.rotation.x = -Math.PI / 2;

      const angle = (i / 6) * Math.PI * 2 + 0.5;
      const distance = 120 + Math.random() * 80;
      parking.position.set(
        Math.cos(angle) * distance,
        -this.floorHeight + 0.02,
        Math.sin(angle) * distance
      );

      this.add(parking);
    }
  }

  addHorizonTrees() {
    // Create tall trees around the horizon edge
    const trunkMaterial = this._unlitMaterial(0x6b4423); // Lighter brown
    const canopyMaterial = this._unlitMaterial(0x3d6b3d); // Lighter green

    // Create multiple layers of trees for dense coverage
    const layers = [
      { distance: 280, count: 200 }, // Inner layer - very dense
      { distance: 310, count: 180 }, // Middle-inner layer
      { distance: 340, count: 160 }, // Middle-outer layer
      { distance: 370, count: 140 }  // Outer layer
    ];

    layers.forEach(layer => {
      for (let i = 0; i < layer.count; i++) {
        const angle = (i / layer.count) * Math.PI * 2;
        const distance = layer.distance + (Math.random() - 0.5) * 30; // Vary distance

        const treeX = Math.cos(angle) * distance;
        const treeZ = Math.sin(angle) * distance;

        // Tall tree dimensions (like pine/fir trees)
        const trunkHeight = 8 + Math.random() * 6; // 8-14m tall
        const trunkRadius = 0.3 + Math.random() * 0.2;
        const canopyHeight = 10 + Math.random() * 5; // Taller canopy
        const canopyRadius = 2.5 + Math.random() * 1.5;

        // Create trunk
        const trunkGeometry = new THREE.CylinderGeometry(
          trunkRadius * 0.8,
          trunkRadius,
          trunkHeight,
          6
        );
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);

        trunk.position.set(
          treeX,
          -this.floorHeight + trunkHeight / 2,
          treeZ
        );

        this.add(trunk);

        // Create canopy (cone shape for tall trees)
        const canopyGeometry = new THREE.ConeGeometry(canopyRadius, canopyHeight, 6);
        const canopy = new THREE.Mesh(canopyGeometry, canopyMaterial);

        canopy.position.set(
          treeX,
          -this.floorHeight + trunkHeight - canopyHeight * 0.3, // Overlap with trunk more
          treeZ
        );

        this.add(canopy);
      }
    });
  }

  createBillboard() {
    // Create a short building with a rooftop billboard on poles
    // Position it at a fixed location visible from the room windows

    // Building dimensions - short building
    const buildingWidth = 20;
    const buildingDepth = 14;
    const buildingHeight = 15; // About 5 floors

    // Billboard dimensions - match logo aspect ratio (800x300 = 8:3)
    const billboardWidth = 12;
    const billboardHeight = 4.5;
    const poleHeight = 2;
    const poleRadius = 0.25;

    // Building position
    const buildingX = 20;
    const buildingZ = 75;

    // Create the building
    const buildingMaterial = new THREE.MeshStandardMaterial({
      color: 0x6a6a6a,
      roughness: 0.85,
      metalness: 0.15
    });
    const buildingGeometry = new THREE.BoxGeometry(buildingWidth, buildingHeight, buildingDepth);
    const building = new THREE.Mesh(buildingGeometry, buildingMaterial);
    building.position.set(buildingX, -this.floorHeight + buildingHeight / 2, buildingZ);
    building.castShadow = true;
    building.receiveShadow = true;
    building.userData.isBuilding = true;
    building.userData.originalColor = 0x6a6a6a;
    this.add(building);

    // Add windows to the building
    this.addWindows(building, buildingWidth, buildingHeight, buildingDepth);

    // Rooftop level
    const rooftopY = -this.floorHeight + buildingHeight;

    // Create two poles on the roof
    const poleMaterial = new THREE.MeshStandardMaterial({
      color: 0x444444,
      roughness: 0.6,
      metalness: 0.4
    });
    const poleGeometry = new THREE.CylinderGeometry(poleRadius, poleRadius, poleHeight, 8);

    const poleSpacing = billboardWidth * 0.4;

    const pole1 = new THREE.Mesh(poleGeometry, poleMaterial);
    pole1.position.set(buildingX - poleSpacing, rooftopY + poleHeight / 2, buildingZ - buildingDepth / 4);
    pole1.castShadow = true;
    this.add(pole1);

    const pole2 = new THREE.Mesh(poleGeometry, poleMaterial);
    pole2.position.set(buildingX + poleSpacing, rooftopY + poleHeight / 2, buildingZ - buildingDepth / 4);
    pole2.castShadow = true;
    this.add(pole2);

    // Create the billboard frame on top of the poles
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.4,
      metalness: 0.6
    });
    const frameGeometry = new THREE.BoxGeometry(billboardWidth + 0.5, billboardHeight + 0.5, 0.4);
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    const billboardY = rooftopY + poleHeight + billboardHeight / 2;
    frame.position.set(buildingX, billboardY, buildingZ - buildingDepth / 4);
    frame.castShadow = true;
    this.add(frame);

    // Create the billboard face with Feydom logo texture
    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load(assetUrl('textures/feydom-logo-red.jpg'));
    logoTexture.colorSpace = THREE.SRGBColorSpace;

    const billboardMaterial = new THREE.MeshBasicMaterial({
      map: logoTexture,
      side: THREE.DoubleSide
    });
    const billboardGeometry = new THREE.PlaneGeometry(billboardWidth, billboardHeight);
    const billboardFace = new THREE.Mesh(billboardGeometry, billboardMaterial);
    billboardFace.position.set(buildingX, billboardY, buildingZ - buildingDepth / 4 - 0.25);
    billboardFace.rotation.y = Math.PI;
    this.add(billboardFace);

    // Store references
    this.billboardFace = billboardFace;
    this.billboardBuilding = building;
  }

  _unlitMaterial(color) {
    const material = new THREE.MeshBasicMaterial({ color });
    this._unlitMaterials.push({ material, baseColor: color });
    return material;
  }

  update(timeOfDay, sunDirection) {
    const unlitScale = UNLIT_BRIGHTNESS[timeOfDay] ?? 1;
    for (const { material, baseColor } of this._unlitMaterials) {
      material.color.setHex(baseColor).multiplyScalar(unlitScale);
    }

    // Update window brightness based on time of day
    this.children.forEach(child => {
      if (child.userData.isBuilding) {
        // Update window colors
        child.children.forEach(window => {
          if (window.material && window.material.color) {
            if (timeOfDay === 'Nighttime') {
              window.material.color.setHex(0xffee88); // Bright at night
            } else if (timeOfDay === 'Sunrise' || timeOfDay === 'Sunset') {
              window.material.color.setHex(0x886644); // Dim warm glow at twilight
            } else {
              window.material.color.setHex(0x000000); // Black during day (no artificial lights)
            }
          }
        });

        // Update building brightness based on stored original color
        if (child.userData.originalColor !== undefined) {
          const originalColor = this._scratchColor.setHex(child.userData.originalColor);

          if (timeOfDay === 'Nighttime') {
            child.material.color.copy(originalColor).multiplyScalar(0.4);
          } else if (timeOfDay === 'Sunrise' || timeOfDay === 'Sunset') {
            child.material.color.copy(originalColor).multiplyScalar(0.8);
          } else {
            // Full brightness during day
            child.material.color.copy(originalColor);
          }
        }
      }

      // Update ground color based on time
      if (child.userData.isGround) {
        if (timeOfDay === 'Nighttime') {
          child.material.color.setHex(0x1a1510);
        } else if (timeOfDay === 'Sunrise' || timeOfDay === 'Sunset') {
          child.material.color.setHex(0x3a2a20);
        } else {
          child.material.color.setHex(0x2a2520);
        }
      }
    });
  }
}
